/**
 * 수동 청산의 패스키 의식 (2026-08-17).
 *
 * 화면에서 "청산" 을 누르면 두 번 왕복한다:
 *   1) close/start  -> 서버가 챌린지와 1회용 티켓을 준다
 *   2) navigator.credentials.get() -> Touch ID / Face ID
 *   3) close/finish -> 서버가 서명을 검증하고 나서야 트레이더에 요청을 넣는다
 *
 * **어느 포지션을 파는지는 티켓에 묶여 서버에만 있다.** finish 는 sid 를 안 보낸다 --
 * 보내면 서명받은 것과 다른 포지션을 팔 수 있다.
 *
 * 실제 매도는 이 왕복이 끝난 뒤 봇이 다음 사이클(5초)에 한다. 즉 여기서 ok 가 와도
 * "주문됨" 이 아니라 "요청이 큐에 들어감" 이다 -- 문구가 그렇게 돼 있어야 한다.
 */
import { analyserPostJSON, analyserFetchJSON } from './analyserApi';

const BASE = '/alert-binance-short-live';

export type PasskeyStatus = {
  enabled: boolean;
  enrolled: number;
  enroll_open: boolean;
};

export type CloseResult = {
  ok: boolean;
  queued?: boolean;
  reason?: string;
  sid?: string;
  perp?: string;
  eta_s?: number;
};

export type EntryResult = CloseResult & {
  /** finish 가 큐에 넣을 때 돌리는 id. status 폴링에 쓴다. */
  req_id?: string;
  lane?: string;
};

export type EntryStatusResult = {
  ok: boolean;
  pending?: boolean;
  reason?: string;
  sid?: string;
  perp?: string;
  result?: {
    ok?: boolean;
    reason?: string;
    holder?: string;
    free?: number;
    need?: number;
    /** 체결된 포지션 sid (수동 청산 버튼이 이걸로 연다). */
    sid?: string;
    perp?: string;
  };
};

export function passkeySupported(): boolean {
  return typeof window !== 'undefined'
    && typeof window.PublicKeyCredential !== 'undefined'
    && !!navigator.credentials;
}

export function fetchPasskeyStatus(): Promise<PasskeyStatus> {
  return analyserFetchJSON<PasskeyStatus>('binance', '/passkey/status', { timeoutMs: 8000 });
}

// WebAuthn 은 ArrayBuffer 를, JSON 은 base64url 을 쓴다. 서버(go-webauthn)도
// base64url 이므로 양쪽에서 이 두 함수만 통과하면 된다.
function b64urlToBuf(s: string): ArrayBuffer {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const bin = window.atob(s.replace(/-/g, '+').replace(/_/g, '/') + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

function bufToB64url(buf: ArrayBuffer | null): string {
  if (!buf) return '';
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return window.btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

type ServerRequestOptions = {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: { type: string; id: string; transports?: string[] }[];
  userVerification?: string;
};

function toRequestOptions(o: ServerRequestOptions): PublicKeyCredentialRequestOptions {
  return {
    challenge: b64urlToBuf(o.challenge),
    timeout: o.timeout,
    rpId: o.rpId,
    userVerification: o.userVerification as UserVerificationRequirement | undefined,
    allowCredentials: (o.allowCredentials ?? []).map((c) => ({
      type: 'public-key',
      id: b64urlToBuf(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  };
}

function serializeAssertion(cred: PublicKeyCredential) {
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufToB64url(r.clientDataJSON),
      authenticatorData: bufToB64url(r.authenticatorData),
      signature: bufToB64url(r.signature),
      userHandle: bufToB64url(r.userHandle),
    },
  };
}

/**
 * 한 포지션을 손으로 판다. 성공하면 "요청이 큐에 들어갔다" 는 뜻이다.
 * 사용자가 Face ID 를 취소하면 NotAllowedError 가 나는데, 그건 실패가 아니라
 * 취소이므로 호출자가 구분해서 조용히 넘길 수 있게 이름을 그대로 던진다.
 */
export async function manualClose(lane: string, sid: string): Promise<CloseResult> {
  if (!passkeySupported()) {
    throw new Error('이 브라우저는 패스키를 지원하지 않는다');
  }
  const started = await analyserPostJSON<{ ticket: string; publicKey: ServerRequestOptions }>(
    'binance', `${BASE}/close/start`, { lane, sid },
  );

  const cred = await navigator.credentials.get({
    publicKey: toRequestOptions(started.publicKey),
  }) as PublicKeyCredential | null;
  if (!cred) throw new Error('인증이 취소됐다');

  return analyserPostJSON<CloseResult>('binance', `${BASE}/close/finish`, {
    ticket: started.ticket,
    assertion: serializeAssertion(cred),
  });
}

/**
 * WATCH/PENDING 건을 손으로 진입시킨다. 성공하면 "요청이 큐에 들어갔다" 는 뜻이다.
 */
export async function manualEntry(lane: string, sid: string): Promise<EntryResult> {
  if (!passkeySupported()) {
    throw new Error('이 브라우저는 패스키를 지원하지 않는다');
  }
  const started = await analyserPostJSON<{ ticket: string; publicKey: ServerRequestOptions }>(
    'binance', `${BASE}/entry/start`, { lane, sid },
  );

  const cred = await navigator.credentials.get({
    publicKey: toRequestOptions(started.publicKey),
  }) as PublicKeyCredential | null;
  if (!cred) throw new Error('인증이 취소됐다');

  return analyserPostJSON<EntryResult>('binance', `${BASE}/entry/finish`, {
    ticket: started.ticket,
    assertion: serializeAssertion(cred),
  });
}

/**
 * 큐에 넣은 수동 진입의 결말. finish 의 req_id 로 폴링한다.
 * pending=true 면 봇이 아직 안 읽음, result.ok=false 면 거절 사유가 있다.
 */
export function fetchEntryStatus(lane: string, reqId: string): Promise<EntryStatusResult> {
  const q = new URLSearchParams({ lane, req_id: reqId });
  return analyserFetchJSON<EntryStatusResult>(
    'binance', `${BASE}/entry/status?${q}`, { timeoutMs: 8000 },
  );
}

export type LaneSettingsLane = {
  id: string;
  label: string;
  live: boolean;
  alloc_pct: number;
  risk_pct: number;
  notional_pct: number;
  k: number;
  tail_pct: number;
  leverage: number;
  margin_pct_per_slot: number;
  margin_pct_lane: number;
  source?: string;
  entry_mode?: 'auto' | 'pullback' | 'cascade_fade';
  ladder_v2?: boolean;
  v1?: {
    k: number;
    alloc_pct: number;
    risk_pct?: number;
    notional_pct?: number;
    margin_pct_per_slot?: number;
    margin_pct_lane?: number;
  };
  v2?: {
    /** 업숏은 사이징에 k 미사용(optional). 청캐 겹침 상한. */
    k?: number;
    blocks_short_lanes?: boolean;
    /** 업숏·빗숏 v2 사이징 (청캐는 [옛] 호환으로만). API 가 항상 채운다. */
    base_notional_pct: number;
    step_notional_pct: number;
    /** 청캐 v2 (2026-09-10): 슬리브 i 증거금% = margin_cap_pct × ratio^i / Σratio^j. */
    margin_cap_pct: number;
    sleeve_ratio?: number;
    /** 러닝고점 대비 이만큼 꺾이면 진입 (0 = 트리거가 즉시). */
    drop_pct?: number;
    /** 청캐 v2 조각 — 슬리브를 max_legs 등분, 신고가가 직전조각 +leg_step_pct% 뛰면 다음 조각. */
    max_legs?: number;
    leg_step_pct?: number;
    /** 깊은꺾임: 진입가가 캐스케이드 고점 −deep_cut_pct% 이상 아래면 ×deep_mult. */
    deep_mult?: number;
    deep_cut_pct?: number;
    /** 고점확인 게이트 · 통과 후 사이징 배수. */
    peak_confirm?: boolean;
    peak_boost_mult?: number;
    preview?: {
      design_depth: number;
      sleeve_ratio?: number;
      shape?: string;
      first_sleeve_margin_pct?: number;
      last_sleeve_margin_pct?: number;
      per_sleeve_margin_pct?: number;
      per_sleeve_notional_pct?: number;
      sleeves?: { sleeve: number; margin_pct: number; notional_pct: number; cum_margin_pct: number; capped: boolean }[];
      notional_pcts: number[];
      margin_sum_pct: number;
      margin_cap_pct: number;
      hits_cap_at?: number | null;
      hits_cap: boolean;
    };
    upbit_preview?: {
      rungs: { rung: number; notional_pct: number; margin_pct: number; capped: boolean }[];
      margin_cap_pct: number;
      cap_notional_pct: number;
      concurrent_rung1: number;
    };
  };
};

export type LaneSettingsPayload = {
  budget_pct: number;
  lanes: LaneSettingsLane[];
  alloc_sum_pct?: number;
  total_slots: number;
  total_margin_pct: number;
  budget_headroom_pct: number;
  updated_at?: string | null;
  updated_by?: string | null;
  has_db_row?: boolean;
};

export function fetchLaneSettings(): Promise<LaneSettingsPayload> {
  return analyserFetchJSON<LaneSettingsPayload>(
    'binance', `${BASE}/lane-settings`, { timeoutMs: 12000 },
  );
}

export async function saveLaneSettings(
  settings: LaneSettingsPayload,
): Promise<LaneSettingsPayload> {
  if (!passkeySupported()) {
    throw new Error('이 브라우저는 패스키를 지원하지 않는다');
  }
  const started = await analyserPostJSON<{ ticket: string; publicKey: ServerRequestOptions }>(
    'binance', `${BASE}/lane-settings/start`, {},
  );
  const cred = await navigator.credentials.get({
    publicKey: toRequestOptions(started.publicKey),
  }) as PublicKeyCredential | null;
  if (!cred) throw new Error('인증이 취소됐다');
  return analyserPostJSON<LaneSettingsPayload>(
    'binance', `${BASE}/lane-settings/finish`, {
      ticket: started.ticket,
      assertion: serializeAssertion(cred),
      settings,
    },
  );
}

export type RetuneApplyResult = {
  ok: boolean;
  keys?: string[];
  message?: string;
  error?: string;
};

export type RetuneRunKind = 'liq' | 'pr' | 'upbit_pb' | 'bh_pb' | 'dual_short';

/** 마지막 대시보드 수동 실행이 실패했을 때(oneshot 유닛 ActiveState=failed).
 *  다음 수동 실행이 성공하면 백엔드가 스스로 null 로 되돌린다. */
export type RetuneRunFail = {
  /** 종료 시각 (ISO UTC). */
  at?: string | null;
  /** 프로세스 종료 코드. */
  exit_status?: string | null;
  /** systemd Result (예: exit-code). */
  result?: string | null;
  /** 로그의 마지막 비어있지 않은 줄 — 대개 예외 한 줄. */
  error?: string | null;
  /** 로그 꼬리(최대 ~1.4KB). */
  log_tail?: string | null;
};

export type RetuneRunLaneStatus = {
  running: boolean;
  /** auto = 타이머·timer service, manual = 대시보드 수동 spawn */
  source?: 'auto' | 'manual' | null;
  duration_note?: string;
  /** 실행 중 경과 초(systemd ActiveEnterTimestamp). */
  elapsed_s?: number | null;
  /** 실행 중이 아닐 때, 직전 수동 실행이 실패했으면 그 사유. */
  last_fail?: RetuneRunFail | null;
};

export type RetuneRunStatusResult = {
  ok: boolean;
  status?: Record<RetuneRunKind, RetuneRunLaneStatus>;
  error?: string;
};

export type RetuneRunSpawnResult = {
  ok: boolean;
  started?: boolean;
  running?: boolean;
  kind?: string;
  message?: string;
  error?: string;
};

/** 청캐 사다리(liqv2) 일일 채점 — analyser_liq_ladder_daily.py 가 적재한 것.
 *  틱(ws_batch)이 4.5일치뿐이라 이 표가 유일한 장기 기록이다. */
export type Liqv2LadderDay = {
  day: string;
  n: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  n_extended: number;
  avg_legs: number;
  /** 그날 잡은 종목 (앞 3개 + 나머지 수) */
  symbols: string;
  /** 그날 채점된 진입이 없으면 false — 칸은 그리되 내용은 비운다 */
  has_data: boolean;
  /** 그날의 레인 건강도 — 규칙이 아니라 청캐 레인 자체 */
  n_trigger: number;
  ord1_avg?: number;
  ord1_win?: number;
  ord3_avg?: number;
  ord3_win?: number;
  ord5_avg?: number;
  ord5_win?: number;
};
/** 레인 건강도 — v2 규칙 성적과 별개. 규칙이 틀렸나 시장이 변했나를 가른다. */
export type Liqv2LadderOrderRow = {
  bucket: string;            // 1st | 2nd | 3rd+ | 5th+
  n: number;
  avg_net_pct?: number;
  win_rate_pct?: number;
};
export type Liqv2LadderHealth = {
  n_trigger: number;
  trigger_per_day: number;
  ladder3: number;
  ladder5: number;
  ladder3_per_day: number;
  by_order: Liqv2LadderOrderRow[];
  /** 뒤쪽 발동이 첫 발동보다 나은가 — v2 의 핵심 전제 */
  order_edge_ok?: boolean;
};

/** 지금 실제로 도는 v2 설정. 규칙은 마지막 채점행의 params, 사이징은
 *  live_lane_settings — 둘 다 실제 소스라 화면이 배포와 어긋나면 드러난다.
 *  값이 없을 수 있으므로 전부 optional (없으면 그 항목만 뺀다). */
export type Liqv2LadderConfig = {
  watch_min?: number;
  drop_pct?: number;
  max_legs?: number;
  leg_step_pct?: number;
  /** 깊은꺾임: 진입가가 캐스케이드 고점 −deep_cut_pct% 이상 아래면 슬리브 ×deep_mult. */
  deep_mult?: number;
  deep_cut_pct?: number;
  /** 순번 정의 (2026-09-09): rung = 캐스케이드 누적 개별청산 // nliq_per_rung.
   *  진입 = 누적청산 ≥ min_cum_nliq. cascade_quiet_h 넘게 안 터지면 새 캐스케이드. */
  nliq_per_rung?: number;
  min_cum_nliq?: number;
  cascade_quiet_h?: number;
  ladder_min_order?: number;
  exit_min?: number;
  extend_min?: number;
  cost_pct?: number;
  same_symbol_max?: number;
  /** 채점 슬롯 = 동시 레일 (lanes.liq.v2.k / params.slot_k). lanes.liq.k(=1)는 옛 v1. */
  slot_k?: number;
  live?: boolean;
  ladder_v2?: boolean;
  alloc_pct?: number;
  budget_pct?: number;
  leverage?: number;
  /** v2 사이징 (2026-09-10): 슬리브 i 증거금% = margin_cap_pct × ratio^i / Σratio^j.
   *  ratio 1 균등 · <1 앞무겁게 · >1 뒤무겁게. 조각 제거. */
  margin_cap_pct?: number;
  sleeve_ratio?: number;
  /** 슬리브별 증거금% (모양 반영, 길이 k). */
  sleeve_margin_pcts?: number[];
  sleeve_margin_pct?: number;      // ratio 1 기준 캡/k
  sleeve_notional_pct?: number;
  /** [옛] score_one/패널 호환 — 사이징엔 안 씀. */
  base_notional_pct?: number;
  step_notional_pct?: number;
  /** 동시 슬리브 k (스택 params.k · SlotK 동의어). */
  k?: number;
  peak_confirm?: boolean;
  peak_boost_mult?: number;
  recover_pct?: number;
  /** 슬롯 꽉 차면 같은종목 연책조각. best_avg = 숏 평단 낮은 쪽. 화면엔 한국어로. */
  overflow_policy?: string;
  /** 2026-10-24 재해헤지 · 촉매 마스터 스위치 */
  catalyst_hold?: boolean;
  disaster_hedge?: boolean;
  disaster_hedge_rule?: string;
  /** 2026-10-25 MED 재해 모드 */
  disaster_med_mode?: boolean;
  disaster_med_rule?: string;
  /** 2026-10-25 잔고밀림 ≤−80 → 전량 연타 청산 */
  eq_stop?: boolean;
  eq_stop_rule?: string;
};

export type Liqv2Ladder = {
  config?: Liqv2LadderConfig;
  n: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  n_extended: number;
  avg_legs: number;
  /** 깊은꺾임 배수 반영 합계(단위슬리브 기준) = Σ net_pct × (deep ? deep_mult : 1). live_full 에만. */
  sum_pct_weighted?: number;
  n_deep?: number;
  days: Liqv2LadderDay[];
  symbols: Record<string, number>;
  last_scored_at?: string;
  /** ok | alarm | hold — 판정선은 서버(파이썬 상수)가 단일 출처다. */
  verdict: string;
  alarms?: string[];
  window_days: number;
  health?: Liqv2LadderHealth;
  /** 라이브스택(고점확인ON) — RECOVER · 촉매정지 · 반사실 */
  live_full?: Liqv2Ladder;
  /** 라이브스택(고점확인OFF) — 지금 라이브 · RECOVER·촉매정지 */
  live_plain?: Liqv2Ladder;
  /** 셀(고점확인OFF) — 고점확인 OFF. 셀(고점확인ON)과 나란히. */
  cell_plain?: Liqv2Ladder;
  /** 실제 원장 live_ledger (lane=liq) */
  ledger?: Liqv2Ladder | null;
  /** 촉매 진입정지 — LSK/ONG형 캘린더. 창 안 심볼 청캐 신규 전면 스킵. */
  catalyst?: Liqv2Catalyst;
  /** 잔고 경로 %eq (책 미실현) — MDD(net 합)와 별개. 자본 $100 CF 앵커. */
  equity?: Liqv2Equity;
};

export type Liqv2SizedPerp = {
  perp: string;
  eq_pct: number;
};

export type Liqv2EquityDay = {
  day: string;
  worst_eq_pct?: number;
  realized_eq_pct?: number;
  liq: boolean;
  liq_at?: string;
  worst_at?: string;
  /** 잔고밀림 시점 오픈 심볼 (미실현 손실 큰 순) */
  worst_perps?: string[];
  /** 강제청산 시점 오픈 심볼 */
  liq_perps?: string[];
  /** 실현%eq 기여 [{perp, eq_pct}, ...] (|eq_pct| 큰 순) */
  sized_perps?: Liqv2SizedPerp[];
  /** 촉매 진입정지 없었으면 강제청산 — 화면 Y 합산 */
  liq_defended?: boolean;
  liq_defended_perps?: string[];
};

export type Liqv2Equity = {
  worst_eq_pct?: number;
  realized_eq_pct?: number;
  liq: boolean;
  liq_at?: string;
  worst_at?: string;
  worst_perps?: string[];
  liq_perps?: string[];
  sized_perps?: Liqv2SizedPerp[];
  liq_defended?: boolean;
  liq_defended_perps?: string[];
  n_sized: number;
  n_skip_cap: number;
  equity_usd: number;
  margin_cap_pct: number;
  k: number;
  days: Liqv2EquityDay[];
  last_scored_at?: string;
};

export type Liqv2CatalystHold = {
  seed_key: string;
  perp: string;
  kind: string;
  from_kst?: string;
  to_kst?: string;
  from_utc?: string;
  to_utc?: string;
  source?: string;
  note?: string;
  active: boolean;
};

export type Liqv2Catalyst = {
  mode?: string;
  last_scored_at?: string;
  active_now: Liqv2CatalystHold[];
  holds: Liqv2CatalystHold[];
  cf?: {
    n_blocked: number;
    pnl_blocked_usdt: number;
    pnl_saved_usdt: number;
    pnl_killed_usdt: number;
    n_holds_live_window: number;
    by_seed?: {
      seed_key: string;
      perp: string;
      kind: string;
      n_blocked: number;
      pnl_blocked_usdt: number;
      pnl_saved_usdt: number;
      pnl_killed_usdt: number;
    }[];
  };
  /** 전방: 홀드 창 안 liq_trigger 수 (진입정지가 막을 후보) */
  forward?: {
    n_triggers: number;
    by_seed?: {
      seed_key: string;
      perp: string;
      kind: string;
      from_kst?: string;
      to_kst?: string;
      n_triggers: number;
      live_now: boolean;
    }[];
  };
  /** 라이브스택 잔고경로 — 촉매 OFF면 강제청산이었는지 */
  equity_defense?: {
    live_full?: {
      liq: boolean;
      liq_defended: boolean;
      worst_eq_pct?: number;
      liq_perps?: string[];
      liq_defended_perps?: string[];
      worst_perps?: string[];
    };
    live_plain?: {
      liq: boolean;
      liq_defended: boolean;
      worst_eq_pct?: number;
      liq_perps?: string[];
      liq_defended_perps?: string[];
      worst_perps?: string[];
    };
  };
  /** 일별 표 행 — 진입정지(트리거)·회피$ */
  days?: {
    day: string;
    n_triggers: number;
    cf_n: number;
    cf_saved_usdt: number;
    cf_killed_usdt: number;
    perps?: string[];
  }[];
  /** 시드 조사 잡 — failed/stale/missing 이면 빨간 배너 */
  seed?: {
    status: 'ok' | 'failed' | 'stale' | 'missing' | string;
    ran_at?: string;
    ok_at?: string;
    detail?: string;
    model?: string;
    host?: string;
    age_hours?: number;
  };
};

export async function fetchLiqv2Ladder(days = 0): Promise<Liqv2Ladder> {
  return analyserFetchJSON<Liqv2Ladder>('binance', `${BASE}/liqv2-ladder?days=${days}`);
}

/** 청캐 v1(되돌림) 고정셀 일일 채점 — analyser_liq_ladder_daily.py --v1-daily. */
export type LiqV1DailyDay = {
  day: string;
  n: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  symbols: string;
  has_data: boolean;
  n_trigger: number;
  ord1_avg?: number;
  ord1_win?: number;
  ord3_avg?: number;
  ord3_win?: number;
};

export type LiqV1DailyHealth = {
  n_trigger: number;
  by_order: Liqv2LadderOrderRow[];
};

export type LiqV1DailyConfig = {
  peak_pct?: number;
  drop_pct?: number;
  watch_min?: number;
  hold_min?: number;
  cost_pct?: number;
  slot_k?: number;
  /** 실동작 live — v1 게이트(v1_enabled) 가 꺼져 있으면 레인 live 여도 false. */
  live?: boolean;
  /** v1 게이트 (liq_v1_enabled, 2026-09-09). false 면 이 채점은 참고용. */
  v1_enabled?: boolean;
  ladder_v2?: boolean;
  k?: number;
  alloc_pct?: number;
  risk_pct?: number;
  leverage?: number;
  notional_pct?: number;
};

export type LiqV1Daily = {
  config?: LiqV1DailyConfig;
  n: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  days: LiqV1DailyDay[];
  symbols: Record<string, number>;
  last_scored_at?: string;
  verdict: string;
  alarms?: string[];
  window_days: number;
  cell: string;
  health?: LiqV1DailyHealth;
  /** 라이브스택 k=∞ — 구조홀드 */
  live_full?: LiqV1Daily;
  /** 실제 원장 live_ledger (lane=liq, v2·v3 포함) */
  ledger?: LiqV1Daily | null;
  /** v1만. v2·v3 정산은 빠진다. */
  ledger_v1?: LiqV1Daily | null;
};

export async function fetchLiqV1Daily(days = 10): Promise<LiqV1Daily> {
  return analyserFetchJSON<LiqV1Daily>('binance', `${BASE}/liq-v1-daily?days=${days}`);
}

/** 청캐 낙하칼 고정셀 일일 채점 — analyser_liq_ladder_daily.py --knife-daily. */
export type LiqKnifeDailyDay = {
  day: string;
  n: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  n_stopped: number;
  avg_hold_min: number;
  symbols: string;
  has_data: boolean;
  n_trigger: number;
  n_armed: number;
};

export type LiqKnifeDailyConfig = {
  thr_pct?: number;
  lag_s?: number;
  max_age_s?: number;
  exit_min?: number;
  stop_pct?: number;
  cost_pct?: number;
  slot_k?: number;
  /** yaml liq_knife_enabled — 감지·무장 자체 */
  enabled?: boolean;
  /** 실주문 = 청캐 레인 live ∧ enabled */
  live?: boolean;
  ladder_v2?: boolean;
  k?: number;
  alloc_pct?: number;
  risk_pct?: number;
  budget_pct?: number;
  leverage?: number;
  notional_pct?: number;
};

export type LiqKnifeDaily = {
  config?: LiqKnifeDailyConfig;
  n: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  n_stopped: number;
  avg_hold_min: number;
  days: LiqKnifeDailyDay[];
  symbols: Record<string, number>;
  last_scored_at?: string;
  verdict: string;
  alarms?: string[];
  window_days: number;
  cell: string;
  n_trigger: number;
  n_armed: number;
};

export async function fetchLiqKnifeDaily(days = 10): Promise<LiqKnifeDaily> {
  return analyserFetchJSON<LiqKnifeDaily>('binance', `${BASE}/liq-knife-daily?days=${days}`);
}

/** 청캐 v3 — v2 사다리와 같은 양식(45일·계·원장·건강도). */
export type LiqV3DailyDay = {
  day: string;
  n: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  n_extended: number;
  avg_hold_min: number;
  symbols: string;
  has_data: boolean;
  n_trigger: number;
  leg1_avg?: number;
  leg1_win?: number;
  leg2_avg?: number;
  leg2_win?: number;
  leg3_avg?: number;
  leg3_win?: number;
  leg4_avg?: number;
  leg4_win?: number;
};

export type LiqV3DailyConfig = {
  gate_cum?: number;
  reenter_every?: number;
  max_entries?: number;
  hold_min?: number;
  extend_min?: number;
  tp_pct?: number;
  recover_pct?: number;
  cost_pct?: number;
  notional_pct?: number;
  margin_cap_pct?: number;
  leverage?: number;
  k?: number;
  enabled?: boolean;
  live?: boolean;
  whitelist?: string[];
  cell?: string;
  /** 2026-10-24 재해헤지 · 촉매 */
  catalyst_hold?: boolean;
  disaster_hedge?: boolean;
  disaster_hedge_rule?: string;
  /** 2026-10-25 MED 재해 모드 */
  disaster_med_mode?: boolean;
  disaster_med_rule?: string;
  /** 2026-10-25 잔고밀림 eq_stop */
  eq_stop?: boolean;
  eq_stop_rule?: string;
};

export type LiqV3DailyHealth = {
  n_trigger: number;
  trigger_per_day: number;
  n_entry: number;
  n_handoff: number;
  by_leg: { bucket: string; n: number; avg_net_pct?: number; win_rate_pct?: number }[];
  reenter_edge_ok?: boolean;
};

export type LiqV3Daily = {
  config?: LiqV3DailyConfig;
  n: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  n_extended: number;
  avg_hold_min: number;
  days: LiqV3DailyDay[];
  symbols: Record<string, number>;
  last_scored_at?: string;
  verdict: string;
  alarms?: string[];
  window_days: number;
  cell: string;
  health?: LiqV3DailyHealth;
  /** 실제 원장 live_ledger · v3만 */
  ledger?: LiqV3Daily | null;
  /** 실현%eq · 잔고밀림% (kind=v3) */
  equity?: Liqv2Equity;
};

export async function fetchLiqV3Daily(days = 0): Promise<LiqV3Daily> {
  return analyserFetchJSON<LiqV3Daily>('binance', `${BASE}/liq-v3-daily?days=${days}`);
}

/** 청캐 통합 실제 원장 — lane=liq 전체 (v1·v2·v3). v3 섹션 아래. */
export type LiqUnifiedLedger = {
  n: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  days: Liqv2LadderDay[];
  symbols?: Record<string, number>;
  window_days: number;
  cell: string;
  equity?: Liqv2Equity;
};

export async function fetchLiqUnifiedLedger(days = 0): Promise<LiqUnifiedLedger> {
  return analyserFetchJSON<LiqUnifiedLedger>(
    'binance', `${BASE}/liq-unified-ledger?days=${days}`);
}

/** 청캐 통합 일일채점 — v2 live_plain(고점OFF) + v3. */
export type LiqUnifiedDaily = {
  n: number;
  n_v2: number;
  n_v3: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  n_extended: number;
  days: Liqv2LadderDay[];
  symbols?: Record<string, number>;
  window_days: number;
  cell: string;
  last_scored_at?: string;
  equity?: Liqv2Equity;
};

export async function fetchLiqUnifiedDaily(days = 0): Promise<LiqUnifiedDaily> {
  return analyserFetchJSON<LiqUnifiedDaily>(
    'binance', `${BASE}/liq-unified-daily?days=${days}`);
}

/** 업숏 되돌림 Top1 일일 채점 — analyser_upbit_pb_daily.py 적재분. */
export type UpbitPbDailyDay = {
  day: string;
  n: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  symbols: string;
  has_data: boolean;
  /** 레인 건강도 */
  lag_med_min: number;
  peak_ok_pct: number;
  drop_ok_pct: number;
  stop_pct: number;
  worst_share_pct: number;
  top1_share_pct: number;
  n_funnel: number;
  n_peak_ok: number;
  has_health: boolean;
  /** 맞춤 칸. 가격% × 명목%. */
  realized_eq_pct?: number | null;
  worst_eq_pct?: number | null;
  sized_perps?: { perp: string; eq_pct: number; kind?: string }[] | null;
};

export type UpbitPbDailyHealth = {
  lag_med_min: number;
  peak_ok_pct: number;
  drop_ok_pct: number;
  stop_pct: number;
  worst_share_pct: number;
  top1_share_pct: number;
  n_funnel: number;
  n_peak_ok: number;
  n_taken: number;
  slot_k: number;
  /** peak 후 drop 진입 — 업숏 Top1 전제 */
  pullback_edge_ok?: boolean;
  /** 진입 중앙이 감시창 안 */
  timing_ok?: boolean;
  /** 빗숏 반사실 책. taken 중 스탑 비중 */
  tail_ok?: boolean;
};

export type UpbitPbDailyConfig = {
  drop_pct?: number;
  watch_min?: number;
  peak_pct?: number;
  hold_min?: number;
  stall_min?: number;
  confirm_s?: number;
  stop_pct?: number;
  fee_pct?: number;
  slot_k?: number;
  live?: boolean;
  k?: number;
  /** lanes.upbit.v2. 채점 slot_k 와 별개. */
  base_pct?: number;
  step_pct?: number;
  margin_cap_pct?: number;
  /** 알림 기준 청산분. 셀 hold_min 과 다르다. */
  hold_exit_min?: number;
  alloc_pct?: number;
  risk_pct?: number;
  budget_pct?: number;
  leverage?: number;
  notional_pct?: number;
};

export type UpbitPbDaily = {
  config?: UpbitPbDailyConfig;
  n: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  n_stop: number;
  lag_med_min: number;
  days: UpbitPbDailyDay[];
  symbols: Record<string, number>;
  last_scored_at?: string;
  verdict: string;
  alarms?: string[];
  window_days: number;
  cell: string;
  /** 맞춤 칸 계. 합계와 다른 축. */
  realized_eq_pct?: number;
  worst_eq_pct?: number;
  health?: UpbitPbDailyHealth;
  /** 라이브스택 반사실 (확인창·구조홀드·스탑OFF · k=∞). */
  live_full?: UpbitPbDaily | null;
  /** 실제 원장 live_ledger (lane=upbit). */
  ledger?: UpbitPbDaily | null;
  /** 진입 후보. 라이브 게이트와 별개. */
  season?: UpbitPbSeason | null;
};

export type UpbitPbSeasonStat = {
  rule: string;
  n: number;
  sum_pct: number;
  avg_net_pct: number;
  win_rate_pct: number;
  worst_pct: number;
};

export type UpbitPbSeasonWindow = {
  label: string;
  n_taken: number;
  rows: UpbitPbSeasonStat[];
};

export type UpbitPbSeasonDay = {
  day: string;
  n: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  realized_eq_pct?: number | null;
  worst_eq_pct?: number | null;
  sized_perps?: { perp: string; eq_pct: number; kind?: string }[] | null;
  has_data: boolean;
};

export type UpbitPbSeasonBook = {
  rule: string;
  n: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  realized_eq_pct: number;
  worst_eq_pct: number;
  days: UpbitPbSeasonDay[];
};

export type UpbitPbSeason = {
  note: string;
  span: string;
  scored_at?: string;
  windows: UpbitPbSeasonWindow[];
  weeks: UpbitPbSeasonStat[];
  book?: UpbitPbSeasonBook | null;
  /** 15분 정리와 9/1 이후 후반 산을 한 자리에 합친 표. 합은 가격%. */
  combo?: UpbitPbSeasonBook | null;
  /** 전승 별책 (Combo ∩ lag<45 · 주문아님 · 본계합산금지). */
  wr100?: UpbitPbSeasonBook | null;
};

export async function fetchUpbitPbDaily(days = 0): Promise<UpbitPbDaily> {
  // days=0 → 적재분 전체 (좌우 스크롤). 양수면 최근 N일.
  return analyserFetchJSON<UpbitPbDaily>('binance', `${BASE}/upbit-pb-daily?days=${days}`);
}

/** 빗숏 화면 책(라이브스택=주문). docs/stream-bh-screen-book-2026-10-10.md */
export type BhPbFits = {
  note: string;
  /** 라이브스택 한 계 = 되돌림(book) + 급락 스캘프(bh_dump). 한 테이블. */
  book?: UpbitPbDaily | null;
  /** 서브행: 되돌림(book)만. */
  pullback?: UpbitPbDaily | null;
  /** 서브행: 급락 스캘프(bh_dump)만. */
  dump?: UpbitPbDaily | null;
  /** 전승 별책 (book ∩ lag<10 · 주문아님 · 본계합산금지). */
  wr100?: UpbitPbDaily | null;
  /** 익절2 별책 (r10≤0∧lag≤30 · TP2/S6/H90 · 스킵 포함 · 주문 OFF · 본계합산금지). */
  tp2?: UpbitPbDaily | null;
};

/** 빗숏 되돌림 일일 채점 — analyser_bh_pb_daily.py (업숏 pb-daily 계약). */
export type BhPbDaily = UpbitPbDaily & {
  fits?: BhPbFits | null;
};

export async function fetchBhPbDaily(days = 0): Promise<BhPbDaily> {
  return analyserFetchJSON<BhPbDaily>('binance', `${BASE}/bh-pb-daily?days=${days}`);
}

/** 편승 일일채점 + 레인 건강도 — analyser_pump_ride_daily.py */
export type PumpRideDailyDay = {
  day: string;
  n: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  symbols: string;
  has_data: boolean;
  lag_med_min: number;
  confirm_ok_pct: number;
  enter_ok_pct: number;
  stop_pct: number;
  n_funnel: number;
  n_confirm_ok: number;
  has_health: boolean;
  rung1_avg?: number | null;
  rung1_win?: number | null;
  rung2_avg?: number | null;
  rung2_win?: number | null;
};

export type PumpRideDailyHealth = {
  lag_med_min: number;
  confirm_ok_pct: number;
  enter_ok_pct: number;
  stop_pct: number;
  n_funnel: number;
  n_confirm_ok: number;
  n_taken: number;
  slot_k: number;
  confirm_edge_ok?: boolean;
  timing_ok?: boolean;
  rung1_avg?: number | null;
  rung1_win?: number | null;
  rung2_avg?: number | null;
  rung2_win?: number | null;
};

export type PumpRideDailyConfig = {
  confirm_pct?: number;
  hold_min?: number;
  watch_max?: number;
  hard_pct?: number;
  trail_arm?: number;
  trail_dd?: number;
  slot_k?: number;
  evolve10?: boolean;
  live?: boolean;
  k?: number;
  alloc_pct?: number;
  risk_pct?: number;
  budget_pct?: number;
  leverage?: number;
  /** 체질 ON — 경로확인 본체만 채점 (스프레이와 섞지 않음). */
  constitution?: boolean;
  path_w?: number;
  path_p?: number;
  rung_policy?: string;
};

export type PumpRideDaily = {
  config?: PumpRideDailyConfig;
  n: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  n_stop: number;
  lag_med_min: number;
  days: PumpRideDailyDay[];
  symbols: Record<string, number>;
  last_scored_at?: string;
  verdict: string;
  alarms?: string[];
  window_days: number;
  cell: string;
  health?: PumpRideDailyHealth;
  live_full?: PumpRideDaily | null;
  ledger?: PumpRideDaily | null;
};

export async function fetchPumpRideDaily(days = 0): Promise<PumpRideDaily> {
  return analyserFetchJSON<PumpRideDaily>('binance', `${BASE}/pump-ride-daily?days=${days}`);
}

/** 겹롱 일일채점. 합·건당은 가격%. 실현%eq 는 업숏과 같은 명목. */
export type GyeopLongDailyDay = {
  day: string;
  n: number;
  avg_net_pct: number;
  win_rate_pct: number;
  worst_pct: number;
  sum_pct: number;
  mdd: number;
  has_data: boolean;
  realized_eq_pct?: number | null;
  worst_eq_pct?: number | null;
  liq?: boolean;
  liq_perps?: string[];
  halt_n?: number;
  halt_perps?: string[];
  sized_perps?: { perp: string; eq_pct: number; kind?: string }[] | null;
};

export type GyeopLongDaily = {
  n: number;
  avg_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  cell: string;
  days: GyeopLongDailyDay[];
  last_scored_at?: string;
  realized_eq_pct?: number | null;
  worst_eq_pct?: number | null;
  liq?: boolean;
  liq_perps?: string[];
  halt_n?: number;
  halt_perps?: string[];
};

export async function fetchGyeopLongDaily(days = 0): Promise<GyeopLongDaily> {
  return analyserFetchJSON<GyeopLongDaily>('binance', `${BASE}/gyeop-long-daily?days=${days}`);
}

/** 업숏 구조홀드(연장) 일일 — depth_gate·giveup 슬리브. */
export type UpbitRescueDailyDay = {
  day: string;
  n: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  delta_sum: number;
  n_depth_gate: number;
  n_would_arm: number;
  symbols: string;
  has_data: boolean;
  has_health: boolean;
};

export type UpbitRescueDailyHealth = {
  n_entered: number;
  n_would_arm: number;
  n_depth_gate: number;
  arm_rate_pct: number;
  gate_rate_pct: number;
  delta_sum: number;
  improve_n: number;
  worsen_n: number;
  edge_ok?: boolean;
};

export type UpbitRescueDaily = {
  n: number;
  avg_net_pct: number;
  med_net_pct: number;
  win_rate_pct: number;
  sum_pct: number;
  worst_pct: number;
  mdd: number;
  delta_sum: number;
  days: UpbitRescueDailyDay[];
  symbols?: Record<string, number>;
  last_scored?: string;
  verdict: string;
  alarms?: string[];
  window_days: number;
  cell: string;
  health?: UpbitRescueDailyHealth;
  live_full?: UpbitPbDaily | null;
  ledger?: UpbitPbDaily | null;
  depth_gate_pct: number;
  giveup_bp: number;
  step_min: number;
};

export async function fetchUpbitRescueDaily(days = 0): Promise<UpbitRescueDaily> {
  return analyserFetchJSON<UpbitRescueDaily>('binance', `${BASE}/upbit-rescue-daily?days=${days}`);
}

export async function fetchRetuneRunStatus(): Promise<RetuneRunStatusResult> {
  return analyserFetchJSON<RetuneRunStatusResult>('binance', `${BASE}/retune-run/status`);
}

/** 재판정 잡 수동 실행 — 패스키. 백그라운드 spawn, 즉시 반환. */
export async function runRetuneJob(
  kind: RetuneRunKind,
  cohortN?: number | null,
): Promise<RetuneRunSpawnResult> {
  if (!passkeySupported()) {
    throw new Error('이 브라우저는 패스키를 지원하지 않는다');
  }
  const started = await analyserPostJSON<{ ticket: string; publicKey: ServerRequestOptions }>(
    'binance', `${BASE}/retune-run/start`, {},
  );
  const cred = await navigator.credentials.get({
    publicKey: toRequestOptions(started.publicKey),
  }) as PublicKeyCredential | null;
  if (!cred) throw new Error('인증이 취소됐다');
  const body: Record<string, unknown> = {
    ticket: started.ticket,
    assertion: serializeAssertion(cred),
    kind,
  };
  if (cohortN != null && Number.isFinite(cohortN) && cohortN > 0) {
    body.cohort_n = Math.floor(cohortN);
  }
  return analyserPostJSON<RetuneRunSpawnResult>(
    'binance', `${BASE}/retune-run/finish`, body,
  );
}

export type RetunePrefsLane = 'upbit_pb' | 'bh_pb' | 'dual_short' | 'pr' | 'liq';

export type RetunePrefsRow = {
  lane: RetunePrefsLane;
  cohort_n: number;
  seed_default: number;
  from_prefs: boolean;
  updated_at?: string | null;
  updated_by?: string | null;
  unit?: 'count' | 'hours';
  label?: string;
  min: number;
  max: number;
};

export type RetunePrefsSaveResult = {
  ok: boolean;
  prefs?: RetunePrefsRow;
  error?: string;
};

/** 되돌림 재판정 격자 건수 — 패스키. 자동·수동 재판정 모두에 적용된다. */
export async function saveRetuneCohort(
  lane: RetunePrefsLane,
  cohortN: number,
): Promise<RetunePrefsSaveResult> {
  if (!passkeySupported()) {
    throw new Error('이 브라우저는 패스키를 지원하지 않는다');
  }
  const started = await analyserPostJSON<{ ticket: string; publicKey: ServerRequestOptions }>(
    'binance', `${BASE}/retune-prefs/start`, {},
  );
  const cred = await navigator.credentials.get({
    publicKey: toRequestOptions(started.publicKey),
  }) as PublicKeyCredential | null;
  if (!cred) throw new Error('인증이 취소됐다');
  return analyserPostJSON<RetunePrefsSaveResult>(
    'binance', `${BASE}/retune-prefs/finish`, {
      ticket: started.ticket,
      assertion: serializeAssertion(cred),
      lane,
      cohort_n: Math.floor(cohortN),
    },
  );
}

/** 재판정 표 행 → 트레이더·연구 yaml (패스키). git 커밋은 별도. */
export async function applyRetuneRow(
  kind: string,
  cell: Record<string, unknown>,
  label?: string,
): Promise<RetuneApplyResult> {
  if (!passkeySupported()) {
    throw new Error('이 브라우저는 패스키를 지원하지 않는다');
  }
  const started = await analyserPostJSON<{ ticket: string; publicKey: ServerRequestOptions }>(
    'binance', `${BASE}/retune-apply/start`, {},
  );
  const cred = await navigator.credentials.get({
    publicKey: toRequestOptions(started.publicKey),
  }) as PublicKeyCredential | null;
  if (!cred) throw new Error('인증이 취소됐다');
  return analyserPostJSON<RetuneApplyResult>(
    'binance', `${BASE}/retune-apply/finish`, {
      ticket: started.ticket,
      assertion: serializeAssertion(cred),
      kind,
      cell,
      label,
    },
    { timeoutMs: 90000 },
  );
}

/**
 * 기기 등록. 평소에는 서버에서 꺼져 있고(MERCURY_PASSKEY_ENROLL_TOKEN 미설정),
 * 맥북/핸드폰을 추가할 때만 켜서 쓴다 -- 등록이 열려 있으면 누구나 자기 패스키를
 * 넣고 버튼을 쓸 수 있다.
 */
export async function enrollPasskey(enrollToken: string, label: string): Promise<void> {
  if (!passkeySupported()) {
    throw new Error('이 브라우저는 패스키를 지원하지 않는다');
  }
  // 라벨은 percent-encoding 해서 보낸다. **HTTP 헤더는 ISO-8859-1 만 담을 수 있어**
  // '맥북' 같은 값을 그대로 실으면 fetch 가 요청을 만들지도 못하고 TypeError 를
  // 던진다(2026-08-17 실측: String contains non ISO-8859-1 code point).
  // 서버가 QueryUnescape 로 되돌린다.
  const headers = {
    'X-Mercury-Enroll-Token': enrollToken,
    'X-Mercury-Device-Label': encodeURIComponent(label),
  };
  const creation = await analyserPostJSON<{
    publicKey: {
      challenge: string;
      rp: { id?: string; name: string };
      user: { id: string; name: string; displayName: string };
      pubKeyCredParams: { type: string; alg: number }[];
      timeout?: number;
      excludeCredentials?: { type: string; id: string }[];
      authenticatorSelection?: AuthenticatorSelectionCriteria;
      attestation?: string;
    };
  }>('binance', '/passkey/register/start', {}, { headers });

  const pk = creation.publicKey;
  const cred = await navigator.credentials.create({
    publicKey: {
      challenge: b64urlToBuf(pk.challenge),
      rp: pk.rp,
      user: {
        id: b64urlToBuf(pk.user.id),
        name: pk.user.name,
        displayName: pk.user.displayName,
      },
      pubKeyCredParams: pk.pubKeyCredParams.map((p) => ({
        type: 'public-key', alg: p.alg,
      })) as PublicKeyCredentialParameters[],
      timeout: pk.timeout,
      excludeCredentials: (pk.excludeCredentials ?? []).map((c) => ({
        type: 'public-key' as const, id: b64urlToBuf(c.id),
      })),
      authenticatorSelection: pk.authenticatorSelection,
      attestation: pk.attestation as AttestationConveyancePreference | undefined,
    },
  }) as PublicKeyCredential | null;
  if (!cred) throw new Error('등록이 취소됐다');

  const r = cred.response as AuthenticatorAttestationResponse;
  await analyserPostJSON('binance', '/passkey/register/finish', {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufToB64url(r.clientDataJSON),
      attestationObject: bufToB64url(r.attestationObject),
    },
  }, { headers });
}
